import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CatalogModel } from 'src/app/model/other-catalogs-models';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { CatalogInfo, CustomResponseObject, PromotionConfig, SaveConfigPromotionRequest, Promocion, PromocionResumen, FiltroMeta, MetaPromocion, MetaPromocionResumen } from 'src/app/model/promotion-models';

@Injectable({
  providedIn: "root",
})
export class CommissionPromotionsService {
  private url: string;

  constructor(private http: HttpClient) {
    this.url = environment.services_url;
  }

  getCatalogPromotionFields() {
    return this.http.get<CatalogModel[]>(
      `${this.url}/CatPromotionFields/catalog`
    );
  }

  // getCatalogPromotionSchemas() {
  //   return this.http.get<CustomResponseObject<CatPromotionSchema[]>>(
  //     `${this.url}/CatPromotionSchemas/catalog`
  //   );
  // }

  getCatalogPromotionSchemasAndConfigurations() {
    return this.http.get<CustomResponseObject<PromotionConfig[]>>(
      `${this.url}/CatPromotionSchemas/catalog`
    );
  }

  savePromotionSchemaConfiguration(
    configPromotion: SaveConfigPromotionRequest
  ) {
    return this.http.post<CustomResponseObject<any>>(
      `${this.url}/CatPromotionSchemas/saveConfiguration`,
      configPromotion
    );
  }

  getRegion() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/region`
    );
  }

  getState() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/state`
    );
  }

  getSucursal() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/sucursal`
    );
  }

  getAsesorType() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/asesorType`
    );
  }

  getCategoryAsesor() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/categoryAsesor`
    );
  }

  getDependency() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/dependency`
    );
  }

  getProduct() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/product`
    );
  }

  getPaymentTerm() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/paymentTerm`
    );
  }

  getSaleType() {
    // Catálogo oficial de Tipo Venta (antes PromotionCatalogs/saleType).
    return this.http.get<CustomResponseObject<any[]>>(
      `${this.url}/CatSaleType/List`
    );
  }

  getGender() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/gender`
    );
  }

  getCivilStatus() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/civilStatus`
    );
  }

  getEmployeeType() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/employeeType`
    );
  }

  getBusinessLine() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/businessLine`
    );
  }

  getClavePromocion() {
    return this.http.get<CustomResponseObject<CatalogInfo[]>>(
      `${this.url}/PromotionCatalogs/clavePromocion`
    );
  }

  savePromotionConfig(promoConfiguration: any, showConfig: any, metas: any[] = []) {
    const userId = localStorage.getItem('com_token');
    const promocion = this.buildPromocionFrom(promoConfiguration, userId, showConfig, metas);
    console.log("Request Save Promotion Data: ", promocion);
    return this.http.post<CustomResponseObject<Promocion>>(
      `${this.url}/promocion/crear`, promocion
    );
  }

  editPromotionConfig(promoConfiguration: any, promotionId: number, showConfig: any, metas: any[] = []) {
    const userId = localStorage.getItem('com_token');
    const promocion = this.buildPromocionFrom(promoConfiguration, userId, showConfig, metas);
    console.log("Request Edit Promotion Data: ", promocion);
    return this.http.put<CustomResponseObject<Promocion>>(
      `${this.url}/promocion/${promotionId}`, promocion
    );
  }

  clonarPromocion(promotionId: number) {
    return this.http.post<CustomResponseObject<PromocionResumen>>(
      `${this.url}/promocion/${promotionId}/clonar`, {});
  }

  getAllPromotions() {
    return this.http.get<CustomResponseObject<PromocionResumen[]>>(
      `${this.url}/promocion/getAll`
    );
  }

  toggleActivePromotion(catPromotion: PromocionResumen) {
    return this.http.put<CustomResponseObject<PromocionResumen>>(
      `${this.url}/promocion/${catPromotion.promocionId}/cambiarEstado`,
      catPromotion
    );
  }

  getPromocion(promotionId: number) {
    return this.http.get<CustomResponseObject<Promocion>>(
      `${this.url}/promocion/${promotionId}`
    );
  }

  /** Flujo completo: crear promoción → metas → filtros */
  crearPromocionCompleta(
    promoConfiguration: any,
    userId: string,
    showConfig: any,
    metas: any[]
  ): Observable<CustomResponseObject<Promocion>> {

    const promocion = this.buildPromocionFrom(promoConfiguration, userId, showConfig, []);

    const nombrePromocion = promoConfiguration.promotionName || 'META';


    return this.http.post<CustomResponseObject<Promocion>>(
      `${this.url}/promocion/crear`, promocion
    ).pipe(
      switchMap((respPromocion) => {
        if (respPromocion.httpStatusCode !== 200 || !metas.length) {
          return of(respPromocion);
        }

        const promocionId = respPromocion.data.promocionId;

        // Crear metas secuencialmente
        return this.crearMetasSecuencialmente(promocionId, metas, respPromocion, nombrePromocion);
      })
    );
  }

  /** Crea metas una por una, encadenando comisiones y filtro de cada una */
  private crearMetasSecuencialmente(
    promocionId: number,
    metas: any[],
    respOriginal: any,
    nombrePromocion: string
  ): Observable<any> {

    // Reducir el array de metas en una cadena de observables secuenciales
    return metas.reduce(
      (cadena$: Observable<any>, meta: any) =>
        cadena$.pipe(
          switchMap(() => this.crearMetaCompleta(promocionId, meta, nombrePromocion))
        ),
      of(respOriginal)   // ← valor inicial de la cadena
    );
  }

  /** Crea una meta + sus comisiones + su filtro */
  private crearMetaCompleta(
    promocionId: number,
    meta: any,
    nombrePromocion: string
  ): Observable<any> {

    const metaPayload = {
      catPromotionId: promocionId,
      fechaInicio: meta.fechaInicio,
      fechaFin: meta.fechaFin,
      fechaCorte: meta.fechaCorte,
      nombrePromocion: meta.nombrePromocion,
      objetivo: meta.objetivo,
      comisiones: (meta.comisiones || []).map((c: any) => ({
        catPromotionId: promocionId,
        plazoValor: c.plazoValor || null,
        categoriaAsesorId: c.categoriaAsesorId || null,
        empleado: c.empleado || null,
        comision: c.comision || 0,
      })),
    };

    return this.http.post<any>(
      `${this.url}/promocion/${promocionId}/meta/crear`, metaPayload
    ).pipe(
      switchMap((respMeta) => {
        if (respMeta.httpStatusCode !== 200) return of(respMeta);

        const metaPromocionId = respMeta.data.metaPromocionId;
        const filtro = meta.filtro || null;

        if (!filtro) return of(respMeta);

        // ← El filtro ya viene en formato backend desde buildFiltroLocal()
        // Solo necesita ajustar el nombre de la estructura y el userId
        const filtroPayload = {
          ...filtro,
          usuarioId: parseInt(localStorage.getItem('UserId') || '0'),
          activoId: 1,
          tipoConfiguracionId: 0,
          tipoConfiguracion: 'ESQUEMA',
          comisionAditiva: 0,
          // ← Asegurar que la estructura tenga el nombre correcto
          estructura: filtro.estructura ? {
            ...filtro.estructura,
            estructuraId: null, // ← null para que el back genere uno nuevo
            nombreEstructura: `${nombrePromocion}_FILTRO_META`,
            usuarioId: parseInt(localStorage.getItem('UserId') || '0'),
            activoId: 1,
            campos: (filtro.estructura.campos || []).map((c: any) => ({
              campoId: c.campoId,
              nombreCampo: c.nombreCampo,
              activoId: c.activoId || 1,
              usuarioId: c.usuarioId || 0,
              fechaCreacion: c.fechaCreacion || null,
            })),
          } : null,
        };

        // ← Verificar que haya al menos un dato relevante antes de enviar
        const tieneDatos = !!(
          (filtroPayload.listRegion && filtroPayload.listRegion.length) ||
          (filtroPayload.listEstado && filtroPayload.listEstado.length) ||
          (filtroPayload.listSucursal && filtroPayload.listSucursal.length) ||
          (filtroPayload.listProducto && filtroPayload.listProducto.length) ||
          (filtroPayload.listDependencia && filtroPayload.listDependencia.length) ||
          (filtroPayload.listTipoAsesor && filtroPayload.listTipoAsesor.length) ||
          (filtroPayload.listLineaNegocio && filtroPayload.listLineaNegocio.length) ||
          (filtroPayload.listGenero && filtroPayload.listGenero.length) ||
          (filtroPayload.listEdad && filtroPayload.listEdad.length) ||
          (filtroPayload.listEstadoCivil && filtroPayload.listEstadoCivil.length) ||
          (filtroPayload.listTipoEmpleado && filtroPayload.listTipoEmpleado.length) ||
          (filtroPayload.listTipoVenta && filtroPayload.listTipoVenta.length) ||
          (filtroPayload.listNuevoCliente && filtroPayload.listNuevoCliente.length) ||
          (filtroPayload.listMontoCapital && filtroPayload.listMontoCapital.length) ||
          (filtroPayload.listTasaAnual && filtroPayload.listTasaAnual.length) ||
          (filtroPayload.listClavePromocion && filtroPayload.listClavePromocion.length) ||
          (filtroPayload.listCategoriaAsesor && filtroPayload.listCategoriaAsesor.length) ||
          (filtroPayload.listPlazo && filtroPayload.listPlazo.length)
        );

        if (!tieneDatos) return of(respMeta);

        return this.http.post<any>(
          `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/filtro/crear`,
          filtroPayload
        ).pipe(map(() => respMeta));
      })
    );
  }

  // Helper para verificar si el filtro tiene datos relevantes
  public filtroTieneDatos(filtro: any): boolean {
    if (!filtro) return false;
    return !!(
      (filtro.typeAsesor && filtro.typeAsesor.length) ||
      (filtro.businessLine && filtro.businessLine.length) ||
      (filtro.gender && filtro.gender.length) ||
      (filtro.civilStatus && filtro.civilStatus.length) ||
      (filtro.typeEmployee && filtro.typeEmployee.length) ||
      (filtro.typeSale && filtro.typeSale.length) ||
      (filtro.nuevoCliente && filtro.nuevoCliente.length) ||
      (filtro.region && filtro.region.length) ||
      (filtro.estado && filtro.estado.length) ||
      (filtro.sucursal && filtro.sucursal.length) ||
      (filtro.dependencia && filtro.dependencia.length) ||
      (filtro.producto && filtro.producto.length) ||
      (filtro.paymentTerms && filtro.paymentTerms.length) ||
      (filtro.categoryAsesor && filtro.categoryAsesor.length) ||
      (filtro.clavePromocion && filtro.clavePromocion.length) || filtro.minAge > 0 ||
      (filtro.maxAge > 0 && filtro.maxAge !== 100) || filtro.minCapitalAmount > 0 ||
      (filtro.maxCapitalAmount > 0 && filtro.maxCapitalAmount !== 10000000) || filtro.tasaAnual > 0
    );
  }

  public buildFiltroPayload(
    filtro: any,
    metaSchema?: any,
    userId?: number,
    filtroExistente?: any,
    nombrePromocion?: string,
    metaShowConfig?: any,
    metaPlazosCatalog?: any[],
    metaPaymentCategoryCatalog?: any[],
    metaCategoriesView?: any[]         // ← NUEVO parámetro
  ): any {
    const userIdFinal = userId || parseInt(localStorage.getItem('UserId') || '0', 10);
    const nombreBase = nombrePromocion ? nombrePromocion.trim() : 'META';
    const nombreEstructuraMeta = metaSchema && metaSchema.promotionSchemaName
      ? metaSchema.promotionSchemaName : `${nombreBase}_FILTRO_META`;
    const estructuraIdExistente = filtroExistente && filtroExistente.estructura
      ? filtroExistente.estructura.estructuraId
      : (metaSchema ? metaSchema.promotionSchemaId || null : null);

    // ── Construir listPlazo y listCategoriaAsesorMeta ─────────────
    let listPlazo: any[] = [];
    let listCategoriaAsesorMeta: any[] = [];

    if (metaShowConfig && metaShowConfig.fieldPaymentTerm && metaShowConfig.fieldCategoryAsesor
      && metaPaymentCategoryCatalog && metaPaymentCategoryCatalog.length) {

      // Plazos x Categorías
      metaPaymentCategoryCatalog.forEach((row: any) => {
        row.paymentCat
          .filter((cell: any) => (cell.comission || 0) > 0)
          .forEach((cell: any) => {
            listPlazo.push({
              plazoId: cell.plazoId || null,
              plazoValor: String(parseInt(row.plazoName, 10)),
              categoriaId: cell.categoryId,
              categoriaNombre: cell.categoryName,
              comision: cell.comission || 0,
            });
          });
      });

    } else if (metaShowConfig && metaShowConfig.fieldPaymentTerm && !metaShowConfig.fieldCategoryAsesor
      && metaPlazosCatalog && metaPlazosCatalog.length) {

      // Solo Plazos
      metaPlazosCatalog
        .filter((p: any) => (p.comission || 0) > 0)
        .forEach((p: any) => {
          listPlazo.push({
            plazoId: p.id,
            plazoValor: String(parseInt(p.name, 10)),
            categoriaId: null,
            comision: p.comission || 0,
          });
        });

    } else if (metaShowConfig && !metaShowConfig.fieldPaymentTerm && metaShowConfig.fieldCategoryAsesor
      && metaCategoriesView && metaCategoriesView.length) {

      // ← Solo las que tienen selected=true
      metaCategoriesView
        .filter((c: any) => c.selected === true)
        .forEach((c: any) => {
          listCategoriaAsesorMeta.push({
            categoriaAsesorId: c.id,
            categoriaAsesorValor: c.name != null ? c.name : c.description
          });
        });
    }

    return {
      estructura: metaSchema ? {
        estructuraId: estructuraIdExistente,
        nombreEstructura: nombreEstructuraMeta,
        usuarioId: userIdFinal,
        activoId: 1,
        campos: (metaSchema.campos || [])
          .filter((c: any) => c.activoId == 1)
          .map((c: any) => ({
            campoId: c.campoId,
            nombreCampo: c.nombreCampo,
            activoId: 1,
            usuarioId: c.usuarioId || 0,
            fechaCreacion: c.fechaCreacion || null,
          }))
      } : null,

      tipoConfiguracionId: 0,
      tipoConfiguracion: 'ESQUEMA',
      comisionAditiva: 0,
      activoId: 1,
      usuarioId: userIdFinal,

      listTipoAsesor: (filtro.typeAsesor || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ tipoAsesorId: e.id, tipoAsesorValor: e.description })),

      // ← Una sola definición: prioriza metaCategoriesView si aplica
      listCategoriaAsesor: listCategoriaAsesorMeta.length
        ? listCategoriaAsesorMeta
        : (filtro.categoryAsesor || [])
          .filter((e: any) => e && e.id !== null && e.id !== undefined)
          .map((e: any) => ({ categoriaAsesorId: e.id, categoriaAsesorValor: e.name != null ? e.name : e.description })),

      listLineaNegocio: (filtro.businessLine || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ lineaNegocioId: e.id, lineaNegocioValor: e.description, core: e.core || null })),

      listGenero: (filtro.gender || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ generoId: e.id, generoValor: e.description })),

      listEdad: (filtro.minAge > 0 || (filtro.maxAge > 0 && filtro.maxAge !== 100))
        ? [{ edadMinima: filtro.minAge || 0, edadMaxima: filtro.maxAge || 100 }]
        : [],

      listEstadoCivil: (filtro.civilStatus || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ estadoCivilId: e.id, core: e.core || null })),

      listTipoEmpleado: (filtro.typeEmployee || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ tipoEmpleadoId: e.id, tipoEmpleadoValor: e.description, core: e.core || null })),

      listTipoVenta: (filtro.typeSale || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ tipoVentaId: e.id, tipoVentaValor: e.description })),

      listNuevoCliente: (filtro.nuevoCliente || [])
        .filter((e: any) => e && e.id !== null && e.id !== undefined)
        .map((e: any) => ({ nuevoClienteId: e.id, nuevoClienteValor: e.description })),

      listRegion: (filtro.region || [])
        .filter((e: any) => e && e._id !== null && e._id !== undefined)
        .map((e: any) => ({ regionId: e._id, regionValor: e._name })),

      listEstado: (filtro.estado || [])
        .filter((e: any) => e && e._id !== null && e._id !== undefined)
        .map((e: any) => ({ estadoId: e._id, estadoValor: e._name })),

      listSucursal: (filtro.sucursal || [])
        .filter((e: any) => e && e._id !== null && e._id !== undefined)
        .map((e: any) => ({ sucursalId: e._id, sucursalValor: e._name })),

      listDependencia: (filtro.dependencia || [])
        .filter((e: any) => e && e._id_int !== null && e._id_int !== undefined)
        .map((e: any) => ({ dependenciaId: e._id_int, dependenciaValor: e._name, core: e.core || null })),

      listProducto: (filtro.producto || [])
        .filter((e: any) => e && e._id_int !== null && e._id_int !== undefined)
        .map((e: any) => ({ productoId: e._id_int, productoValor: e._name, core: e.core || null })),

      listClavePromocion: (filtro.clavePromocion || [])
        .filter((e: any) => e && e._id_int !== null && e._id_int !== undefined)
        .map((e: any) => ({ clavePromocionId: e._id_int })),

      listMontoCapital: (filtro.minCapitalAmount > 0 ||
        (filtro.maxCapitalAmount > 0 && filtro.maxCapitalAmount !== 10000000))
        ? [{
          montoCapitalMinimo: filtro.minCapitalAmount || 0,
          montoCapitalMaximo: filtro.maxCapitalAmount || 10000000
        }]
        : [],

      listTasaAnual: filtro.tasaAnual > 0 ? [{ tasaAnual: filtro.tasaAnual }] : [],
      listPlazo,
      listMontoVenta: [],
      listRefis: [],
      listPagoAsesor: [],
    };
  }

  // ← Helper privado para normalizar fechas sin pérdida de día
  public normalizarFecha(fecha: string): string {
    if (!fecha) return fecha;
    // Si ya es YYYY-MM-DD, devolverla tal cual sin pasar por Date
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
    // Si tiene T, extraer solo la parte de fecha
    console.log("Fecha normalizada: ", fecha.split('T')[0]);
    return fecha.split('T')[0];
  }

  buildPromocionFrom(promoConfiguration: any, userId: string, showConfig: any, metas: any[] = []): Promocion {
    const buildComisionConfiguracion = (): any[] => {
      const lista = [];
      if (showConfig.fieldPaymentTerm && showConfig.fieldCategoryAsesor) {
        promoConfiguration.paymentTerms.forEach((term: any) => {
          if (term.comission > 0) {
            lista.push({
              plazoValor: term.id,
              categoriaAsesorId: term.categoryId || null,
              empleado: null,
              comision: term.comission
            });
          }
        });
      } else if (showConfig.fieldPaymentTerm && !showConfig.fieldCategoryAsesor) {
        promoConfiguration.paymentTerms.forEach((term: any) => {
          if (term.comission > 0) {
            lista.push({
              plazoValor: term.id,
              categoriaAsesorId: null,
              empleado: null,
              comision: term.comission
            });
          }
        });
      } else if (showConfig.fieldAsesor) {
        promoConfiguration.asesores.forEach((asesor: any) => {
          lista.push({
            plazoValor: asesor.plazoId || null,
            categoriaAsesorId: null,
            empleado: asesor.asesorId,
            comision: asesor.comission
          });
        });
      } else if (promoConfiguration.comisionPromotion != null) {
        lista.push({
          plazoValor: null,
          categoriaAsesorId: null,
          empleado: null,
          comision: promoConfiguration.comisionPromotion
        });
      }
      return lista;
    };

    return {

      nombrePromocion: promoConfiguration.promotionName,
      fechaVigenciaInicio: this.normalizarFecha(promoConfiguration.effectiveSince),
      fechaVigenciaFin: this.normalizarFecha(promoConfiguration.effectiveDate),
      comision: promoConfiguration.comisionPromotion,
      comisionAditiva: promoConfiguration.typeComission,
      observaciones: promoConfiguration.observations,
      tipoConfiguracionId: promoConfiguration.typeConfiguration.id,
      tipoConfiguracion: promoConfiguration.typeConfiguration.description,
      exclusivaAsesores: promoConfiguration.exclusivaAsesores,
      clavePromocionActiva: promoConfiguration.clavePromocion.length > 0 ? 1 : 0,
      usuarioId: userId ? parseInt(userId, 10) : null,

      estructura: {
        estructuraId: promoConfiguration.schema.promotionSchemaId,
        nombreEstructura: promoConfiguration.schema.promotionSchemaName,
        usuarioId: userId ? parseInt(userId, 10) : null,
        activoId: 1,
        campos: (promoConfiguration.schema.campos && promoConfiguration.schema.campos.length
          ? promoConfiguration.schema.campos
          : promoConfiguration.schema.fields || [])
          .filter((c: any) => c.activoId == 1)
          .map((c: any) => ({
            campoId: c.campoId != null ? c.campoId : c.promotionFieldId,
            nombreCampo: c.nombreCampo != null ? c.nombreCampo : c.fieldName,
            activoId: c.activoId != null ? c.activoId : 1,
            usuarioId: c.usuarioId != null ? c.usuarioId : (c.userId != null ? c.userId : 0),
            fechaCreacion: c.fechaCreacion != null ? c.fechaCreacion : (c.createDate != null ? c.createDate : null),
          }))
      },

      // ── listRegion ──────────────────────────────────────────────
      listRegion: showConfig.fieldRegion
        ? promoConfiguration.region.map((e: any) => ({
          regionId: e._id,
          regionValor: e._name      // ← antes regionValue
        }))
        : [],

      // ── listEstado ──────────────────────────────────────────────
      listEstado: showConfig.fieldState
        ? promoConfiguration.estado.map((e: any) => ({
          estadoId: e._id,
          estadoValor: e._name      // ← antes stateValue
        }))
        : [],

      // ── listSucursal ────────────────────────────────────────────
      listSucursal: showConfig.fieldSucursal
        ? promoConfiguration.sucursal.map((e: any) => ({
          sucursalId: e._id,
          sucursalValor: e._name    // ← antes sucursalValue
        }))
        : [],

      // ── listTipoAsesor ──────────────────────────────────────────
      listTipoAsesor: showConfig.fieldAsesorType
        ? (promoConfiguration.typeAsesor || []).map((e: any) => ({
          campoTipoAsesorId: e.campoTipoAsesorId || null,
          promocionId: e.promocionId || null,
          tipoAsesorId: e.id,
          tipoAsesorValor: e.description,
        }))
        : [],

      // ── listCategoriaAsesor ─────────────────────────────────────
      listCategoriaAsesor: showConfig.fieldCategoryAsesor
        ? promoConfiguration.categoryAsesor.map((e: any) => ({
          categoriaAsesorId: e.id,
          categoriaAsesorValor: e.name != null ? e.name : e.description  // ← antes solo categoriaAsesorId
        }))
        : [],

      // ── listDependencia ─────────────────────────────────────────
      listDependencia: showConfig.fieldDependency
        ? promoConfiguration.dependencia.map((e: any) => ({
          dependenciaId: e._id_int,
          dependenciaValor: e._name,   // ← antes dependencyValue
          core: e.core
        }))
        : [],

      // ── listProducto ─────────────────────────────────────────────
      // Producto es catálogo local: identidad únicamente por _id_int (nunca _id, que es
      // solo el consecutivo visual del dual listbox). No se envía core.
      listProducto: showConfig.fieldProduct
        ? promoConfiguration.producto.map((product: any) => ({
          productoId: product._id_int,
          productoValor: product._name
        }))
        : [],

      // ── listPlazo ───────────────────────────────────────────────
      listPlazo: showConfig.fieldPaymentTerm
        ? promoConfiguration.paymentTerms.map((e: any) => ({
          plazoId: e.id,
          plazoValor: String(e.name),
          categoriaId: (e.categoryId !== undefined && e.categoryId !== null) ? e.categoryId : null,
          categoriaNombre: e.categoryName,
          comision: e.comission     // ← antes comission (igual)
        }))
        : [],

      // ── listMontoVenta ──────────────────────────────────────────
      listMontoVenta: showConfig.fieldSaleAmount
        ? [{
          valorMinimoVenta: promoConfiguration.minValueSale,      // ← antes minValueSale
          porcentajeDiferencia: promoConfiguration.percentDiference,  // ← antes percentDiference
          fechaInicioBase: promoConfiguration.baseInitDate,      // ← antes baseInitDate
          fechaFinBase: promoConfiguration.baseEndDate,       // ← antes baseEndDate
          fechaInicioComparacion: promoConfiguration.compareInitDate,  // ← antes compareInitDate
          fechaFinComparacion: promoConfiguration.compareEndDate     // ← antes compareEndDate
        }]
        : [],

      // ── listRefis ───────────────────────────────────────────────
      listRefis: showConfig.fieldRefi
        ? [{ porcentajeAvance: promoConfiguration.advantageRefi }]      // ← antes advancedPercent
        : [],

      // ── listTipoVenta ───────────────────────────────────────────
      listTipoVenta: showConfig.fieldSaleType
        ? promoConfiguration.typeSale.map((e: any) => ({
          tipoVentaId: e.id,
          tipoVentaValor: e.description  // ← antes saleTypeValue
        }))
        : [],

      // ── listGenero ──────────────────────────────────────────────
      listGenero: showConfig.fieldGender
        ? promoConfiguration.gender.map((e: any) => ({
          generoId: e.id,
          generoValor: e.description     // ← antes genderValue
        }))
        : [],

      // ── listEdad ────────────────────────────────────────────────
      listEdad: showConfig.fieldAge
        ? [{
          edadMinima: promoConfiguration.minAge,  // ← antes minAge
          edadMaxima: promoConfiguration.maxAge   // ← antes maxAge
        }]
        : [],

      // ── listEstadoCivil ─────────────────────────────────────────
      listEstadoCivil: showConfig.fieldCivilStatus
        ? promoConfiguration.civilStatus.map((e: any) => ({
          estadoCivilId: e.id,
          estadoCivilValor: e.description,  // ← antes civilStatusValue
          core: e.core
        }))
        : [],

      // ── listTipoEmpleado ────────────────────────────────────────
      listTipoEmpleado: showConfig.fieldEmployeeType
        ? promoConfiguration.typeEmployee.map((e: any) => ({
          tipoEmpleadoId: e.id,
          tipoEmpleadoValor: e.description,  // ← antes employeeTypeValue
          core: e.core
        }))
        : [],

      // ── listLineaNegocio ────────────────────────────────────────
      listLineaNegocio: showConfig.fieldBusinessLine
        ? promoConfiguration.businessLine.map((e: any) => ({
          lineaNegocioId: e.id,
          lineaNegocioValor: e.description,  // ← antes businessLineValue
          core: e.core
        }))
        : [],

      // ── listMontoCapital ────────────────────────────────────────
      listMontoCapital: showConfig.fieldCapitalAmount
        ? [{
          montoCapitalMinimo: promoConfiguration.minCapitalAmount,  // ← antes minCapitalAmount
          montoCapitalMaximo: promoConfiguration.maxCapitalAmount   // ← antes maxCapitalAmount
        }]
        : [],

      // ── listTasaAnual ───────────────────────────────────────────
      listTasaAnual: showConfig.fieldTasaAnual
        ? [{ tasaAnual: promoConfiguration.tasaAnual, modo: promoConfiguration.modoTasaAnual }]
        : [],

      // ── listClavePromocion ──────────────────────────────────────
      listClavePromocion: showConfig.fieldClavePromocion
        ? promoConfiguration.clavePromocion.map((e: any) => ({
          clavePromocionId: e._id_int,
          clavePromocionValor: e._name    // ← antes clavePromocionValue
        }))
        : [],

      // ── listNuevoCliente ────────────────────────────────────────
      listNuevoCliente: showConfig.fieldNuevoCliente
        ? promoConfiguration.nuevoCliente.map((e: any) => ({
          nuevoClienteId: e.id,
          nuevoClienteValor: e.description  // ← antes nuevoClienteValue
        }))
        : [],

      // ── listPagoAsesor ──────────────────────────────────────────
      listPagoAsesor: showConfig.fieldAsesor
        ? promoConfiguration.asesores.map((e: any) => ({
          asesorId: e.asesorId,
          comision: e.comission,   // ← antes comission
          plazoId: e.plazoId,
          plazoName: e.plazoName
        }))
        : [],
    };
  }

  private buildComisionConfiguracion(promoConfiguration: any, showConfig: any): any[] {
    const lista = [];
    console.log("Construyendo comisionConfiguracion con showConfig:", showConfig);
    if (showConfig.fieldPaymentTerm && showConfig.fieldCategoryAsesor) {
      // Plazos x Categorías
      promoConfiguration.paymentTerms.forEach((term: any) => {
        if (term.comission > 0) {
          lista.push({
            plazoValor: term.id,
            categoriaAsesorId: term.categoryId || null,
            empleado: null,
            comision: term.comission
          });
        }
      });

    } else if (showConfig.fieldPaymentTerm && !showConfig.fieldCategoryAsesor) {
      // Solo plazos
      promoConfiguration.paymentTerms.forEach((term: any) => {
        if (term.comission > 0) {
          lista.push({
            plazoValor: term.id,
            categoriaAsesorId: null,
            empleado: null,
            comision: term.comission
          });
        }
      });

    } else if (showConfig.fieldAsesor) {
      // Asesores
      promoConfiguration.asesores.forEach((asesor: any) => {
        lista.push({
          plazoValor: asesor.plazoId || null,
          categoriaAsesorId: null,
          empleado: asesor.asesorId,
          comision: asesor.comission
        });
      });

    } else if (promoConfiguration.comisionPromotion != null) {
      // Comisión general sin plazos ni categorías
      lista.push({
        plazoValor: null,
        categoriaAsesorId: null,
        empleado: null,
        comision: promoConfiguration.comisionPromotion
      });
    }

    return lista;
  }

  // ── Métodos nuevos e implementacion ───────────────────────────────────────────────
  // ── MetaPromocion ──────────────────────────────────────────────
  /** POST /promocion/{promocionId}/meta/crear */
  crearMeta(promocionId: number, meta: MetaPromocion) {
    return this.http.post<CustomResponseObject<MetaPromocionResumen>>(
      `${this.url}/promocion/${promocionId}/meta/crear`,
      meta
    );
  }

  /** PUT /promocion/{promocionId}/meta/{metaPromocionId} */
  editarMeta(promocionId: number, metaPromocionId: number, meta: MetaPromocion) {
    return this.http.put<CustomResponseObject<MetaPromocion>>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}`,
      meta
    );
  }

  /** DELETE /promocion/{promocionId}/meta/{metaPromocionId} */
  eliminarMeta(promocionId: number, metaPromocionId: number) {
    return this.http.delete<CustomResponseObject<void>>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}`
    );
  }

  toggleActivarMeta(promocionId: number, metaPromocionId: number, activar: boolean) {
    return this.http.get<CustomResponseObject<MetaPromocionResumen>>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/cambiarEstado?activar=${activar}`);
  }

  // ── FiltroMeta ─────────────────────────────────────────────────

  /** Endpoint para comisión individual de una meta */
  crearComisionMeta(
    promocionId: number,
    metaPromocionId: number,
    comision: any
  ): Observable<any> {
    return this.http.post<any>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/comision/crear`,
      comision
    );
  }

  /** Endpoint para filtro de una meta */
  crearFiltroMeta(
    promocionId: number,
    metaPromocionId: number,
    filtro: any
  ): Observable<any> {
    return this.http.post<any>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/filtro/crear`,
      filtro
    );
  }

  /** PUT /promocion/{promocionId}/meta/{metaPromocionId}/filtro/{filtroMetaId} */
  editarFiltroMeta(
    promocionId: number,
    metaPromocionId: number,
    filtroMetaId: number,
    filtro: any
  ): Observable<any> {
    return this.http.put<any>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/filtro/${filtroMetaId}`,
      filtro
    );
  }

  /** DELETE /promocion/{promocionId}/meta/{metaPromocionId}/filtro/{filtroMetaId} */
  eliminarFiltroMeta(promocionId: number, metaPromocionId: number, filtroMetaId: number) {
    return this.http.delete<CustomResponseObject<void>>(
      `${this.url}/promocion/${promocionId}/meta/${metaPromocionId}/filtro/${filtroMetaId}`
    );
  }
}
